import {AnimationBaseController, IAnimationControllerConfiguration, Inject} from '@denishussar/html5-game-engine';
import {Point} from 'pixi.js';
import {AnimationContents} from '../../../AnimationContents';
import {ButtonController, IButtonControllerOptions} from '../../Common/ButtonController';
import {GameLogic} from '../../Logic/GameLogic';
import {TopPanelLogic} from '../../Logic/TopPanelLogic';
import {GameModel, GameStateEnum} from '../../Models/GameModel';
import {SettingsMenuController} from '../UI/SettingsMenuController';
import {IUICoinsControllerOptions, UICoinsController} from './Main/UICoinsController';
import {IUILivesControllerOptions, UILivesController} from './Main/UILivesController';
import {TitleTextStyle} from '../../Constants/TextStyles';
import {ModalsNames} from "../../Common/MadalsManager/ModalsList";

export interface ITopBarMenuControllerOptions {
    title?: string;
    showLives?: boolean;
    showCoins?: boolean;
    showSettings?: boolean;
    showExitButton?: boolean;
    showCloseButton?: boolean,
    showPauseButton?: boolean;
    isGameScreenParent?: boolean;
    hideExitButtonInSettings?: boolean;

    onClickExit: (status: boolean) => void;
    onClickMusic: (status: boolean) => void;
    onClickSound: (status: boolean) => void;
}

export class TopBarMenuController extends AnimationBaseController {
    $model: GameModel;

    public constructor(
        @Inject private _options: ITopBarMenuControllerOptions,
        @Inject private _gameLogic: GameLogic,
        @Inject private _topPanelLogic: TopPanelLogic,
    ) {
        super('TopBarMenuController');
    }

    public coins: UICoinsController;

    private _labelPositionX: number | undefined;
    private _btnSettingsClosedWidth: number;
    private _paletteWidth: number = 0;

    public set label(value: string) {
        this.setTextByQuery('txt_title', value);
        this.setTextStyleByQuery('txt_title', TitleTextStyle);

        let title = this.getDisplayObjectByQuery('txt_title')[0] as PIXI.Text;
        title.visible = value.length > 0;
        title.text = value;

        // title = this.getDisplayObjectByQuery('txt_title_shadow')[0] as PIXI.Text;
        // title.visible = value.length > 0;
        // title.text = value;
    }

    public set labelX(value: number) {
        this._labelPositionX = value;

        this.getDisplayObjectByQuery('txt_title')[0].x = value;
        // this.getDisplayObjectByQuery('txt_title_shadow')[0].x = value + 1;
        // this.getDisplayObjectByQuery('txt_title_shadow')[0].y = this.getDisplayObjectByQuery('txt_title')[0].y + 3;
    }

    protected getConfiguration(): IAnimationControllerConfiguration {
        return {
            animationContent: AnimationContents.TOP_BAR_MENU_JSON,
            libraryName: 'main',
            childControllers: {
                lives: {
                    instanceName: 'leave_container', // TODO rename the key in the FLA
                    controller: UILivesController,
                    model: this.$model,
                    options: <IUILivesControllerOptions>{
                        //onClick: () => this._topPanelLogic.handleClickOnLivesIndicator(),
                    },
                },
                coins: {
                    instanceName: 'coins_container',
                    controller: UICoinsController,
                    model: this.$model,
                    options: <IUICoinsControllerOptions>{
                        //onClick: () => this._topPanelLogic.handleClickOnCoinsIndicator(),
                    },
                },
                // btn_pause_container: {
                //     instanceName: 'btn_pause_container',
                //     controller: ToggleButtonController,
                //     model: this.$model,
                //     options: <IToggleButtonControllerOptions>{
                //         onOff: false,
                //     },
                // },
                btn_settings: {
                    instanceName: 'btn_settings_container',
                    controller: SettingsMenuController,
                    model: this.$model,
                    options: {
                        onClickExit: (status: boolean) => this._options.onClickExit(status),
                        hideExitButton: this._options.hideExitButtonInSettings,
                    },
                },
                btn_exit: {
                    instanceName: 'btn_exit',
                    controller: ButtonController,
                    options: <IButtonControllerOptions>{
                        onClick: (status: boolean) => this._options.onClickExit(status),
                    },
                },
                btn_close: {
                    instanceName: 'btn_close',
                    controller: ButtonController,
                    options: <IButtonControllerOptions>{
                        onClick: (status: boolean) => this._options.onClickExit(status),
                    },
                },
            },
            modelEvents: {
                changeModal: (state: string, key: string, value: boolean) => {
                    if (this.$model.gameState === GameStateEnum.game) {
                        const isOpenedModal = this._gameLogic.modalsManager.hasView(ModalsNames.shop) || this._gameLogic.modalsManager.hasView(ModalsNames.shopCrazyGames);

                        this.$childControllers.get('btn_settings').visible = !isOpenedModal;
                        this.$childControllers.get('coins').visible = this._gameLogic.isFTUE ? false : this._options.isGameScreenParent ? !isOpenedModal : isOpenedModal;
                    }
                }
            }
        }
    }

    protected onInitialize() {
        this.label = this._options.title || '';

        // this.setTextStyleByQuery('txt_title', { stroke: '#a83300', strokeThickness: 8 });
        // this.setTextStyleByQuery('txt_title_shadow', { stroke: '#a83300', strokeThickness: 8, fill: '#a83300' });

        const btn_exit = this.$childControllers.get('btn_exit').display;
        btn_exit.visible = this._options.showExitButton === true;
        btn_exit.interactive = btn_exit.visible;

        const btn_close = this.$childControllers.get('btn_close').display;
        btn_close.visible = this._options.showCloseButton === true;
        btn_close.interactive = btn_close.visible;

        const btnSettings = this.$childControllers.get('btn_settings').display;
        btnSettings.visible = this._options.showSettings === true;
        btnSettings.interactive = btnSettings.visible;

        const lives = this.$childControllers.get('lives').display;
        lives.visible = this._options.showLives === true;

        this.coins = this.$childControllers.get('coins');
        this.coins.visible = this._options.showCoins === true;

        this.showPauseButton(this._options.showPauseButton === true);

        this._btnSettingsClosedWidth = btnSettings.width;
    }

    public setPaletteWidth(value: number): void {
        this._paletteWidth = value;
    }

    protected onResize(width: number, height: number, scale: number) {
        const margin = 20;

        const x = this._paletteWidth;

        const topLeftPoint = this.display.toLocal(new Point(x, 0), this.renderer.stage);
        const topRightPoint = this.display.toLocal(new Point(width, 0), this.renderer.stage);
        const bottomLeftPoint = this.display.toLocal(new Point(x, height), this.renderer.stage);
        const bottomRightPoint = this.display.toLocal(new Point(width, height), this.renderer.stage);
        const bottomCenterPoint = this.display.toLocal(new Point(width / 2, height), this.renderer.stage);

        const btn_exit = this.$childControllers.get('btn_exit').display;
        const btn_close = this.$childControllers.get('btn_close').display;
        const btnSettings = this.$childControllers.get('btn_settings').display;
        const lives = this.$childControllers.get('lives').display;
        const coins = this.$childControllers.get('coins').display;

        btnSettings.x = topRightPoint.x - this._btnSettingsClosedWidth + margin * 1.5;
        btnSettings.y = topRightPoint.y + this._btnSettingsClosedWidth / 3;

        btn_exit.x = btnSettings.x - btnSettings.width / 2;
        btn_exit.y = btnSettings.y + btnSettings.height / 2;

        btn_close.x = btn_exit.x;
        btn_close.y = btn_exit.y;

        lives.x = topLeftPoint.x + lives.width / 2 + margin;
        lives.y = topLeftPoint.y + lives.height / 2 + margin;

        coins.x = lives.x + (lives.visible ? lives.width : 0);
        coins.y = topLeftPoint.y + coins.height / 2 + margin;

        this.setPosByQuery('btn_pause_container', topLeftPoint.x + margin, topLeftPoint.y + margin);
        this.setPosByQuery('pause_container', topLeftPoint.x + margin, topLeftPoint.y + margin);
        const title = this.getDisplayObjectByQuery('txt_title')[0] as PIXI.Text;

        if (this._labelPositionX !== undefined) {
            this.labelX = this._labelPositionX;
        } else {
            this.labelX = -title.width / 2;
        }
        this.setPosByQuery('txt_title', title.x, topLeftPoint.y + margin);
        // this.getDisplayObjectByQuery('txt_title_shadow')[0].y = title.y + 3;
    }

    private showPauseButton(value: boolean): void {
        this.getDisplayObjectByQuery('btn_pause_container').forEach((element) => {
            element.interactive = value;
            element.alpha = value ? 1 : 0;
        });
        this.getDisplayObjectByQuery('pause_container').forEach((element) => {
            element.interactive = value;
            element.alpha = value ? 1 : 0;
        });
    }
}
